import { AdminDeletedItem, ArchivelogsResponse, Document, Subscription, UserExtraData, UserProfile, UserRole } from "../../models";
import { UserToken } from "../../types/token/token";


interface AdminResult {
    Value: {
        Source: Document
    }
}

interface AdminResponse {
    data: AdminResult[]
    count: number
}

type AdminItemResponse = Document | null

const initSearchResponse: AdminResponse = {
    data: [],
    count: 0
}

interface AdminOptions {
    abortController?: AbortController | null
    returnFullData?: boolean
}

export default class AdminService {
    #token: UserToken = null
    #url: string = ''
    constructor(token: UserToken) {
        this.#token = token;
    }

    getHeaders() {
        return {
            'Authorization': 'Bearer ' + this.#token
        };
    }

    postHeaders() {
        return {
            'Authorization': 'Bearer ' + this.#token,
            'Content-Type': 'application/json'
        };
    }

    setToken = (token: UserToken, url?: string) => {
        this.#token = token;
        this.#url = url ?? '';
    }

    getToken() {
        return this.#token;
    }

    async getLogItems() {
        try {
            const address = `${this.#url}/archive/logs`
            const res = await fetch(address, {
                method: 'GET',
                headers: this.getHeaders(),
            })
               
            const result: ArchivelogsResponse = await res.json()
            if(!result) return { val: null, isAborted: false }
            return { val: result, isAborted: false }
            
        }
        catch(err) {
            //@ts-ignore
            if (err.name === 'AbortError') {
                return { val: null, isAborted: true }
            }
            
            return { val: null, isAborted: false }
        }
    }

    async getDeletedItems() {
        try {
            const address = `${this.#url}/archive/deleted`
            const res = await fetch(address, {
                method: 'GET',
                headers: this.getHeaders(),
            })
               
            const result: AdminDeletedItem[] = await res.json()
            if(!result?.length) return { val: [], isAborted: false }
            return { val: result, isAborted: false }
            
        }
        catch(err) {
            //@ts-ignore
            if (err.name === 'AbortError') {
                return { val: [], isAborted: true }
            }
            
            return { val: [], isAborted: false }
        }
    }

    async getCurrentSubscription() {
        try {
            const address = `${this.#url}/billing/subscription`
            const res = await fetch(address, {
                method: 'GET',
                headers: this.getHeaders(),
            })
               
            const result: Subscription = await res.json()
            if(!('subscriptionType' in result)) return { val: null, isAborted: false }
            return { val: result, isAborted: false }
            
        }
        catch(err) {
            //@ts-ignore
            if (err.name === 'AbortError') {
                return { val: null, isAborted: true }
            }
            
            return { val: null, isAborted: false }
        }
    }

    async restoreItem(docId: string, onSuccess: (docId: string) => void, onFail: () => void) {
        try {
            const address = `${this.#url}/archive/restore/${docId}`
            const res = await fetch(address, {
                method: 'GET',
                headers: this.getHeaders(),
            })
               
            const result = await res.json()
            if(!result || result?.status !== 'Success') {
                onFail()
                return
            }
            onSuccess(docId)
        }
        catch(err) {
            onFail()
        }
    }

    async restoreUser(userId: number, onSuccess: () => void, onFail: () => void) {
        try {
            const address = `${this.#url}/adminusersettings/restoreUser?userId=${userId}`
            const res = await fetch(address, {
                method: 'PUT',
                headers: this.getHeaders(),
            })
               
            if(res.status !== 200) {
                onFail()
                return
            }
            onSuccess()
        }
        catch(err) {
            onFail()
        }
    }

    async deleteUser(userId: number, onSuccess: () => void, onFail: () => void) {
        try {
            const address = `${this.#url}/adminusersettings?userId=${userId}`
            const res = await fetch(address, {
                method: 'DELETE',
                headers: this.getHeaders(),
            })
               
            if(res.status !== 200) {
                onFail()
                return
            }
            onSuccess()
        }
        catch(err) {
            onFail()
        }
    }

    async editUser(data: UserProfile & UserExtraData, onSuccess: () => void, onFail: () => void) {
        try {
            const address = `${this.#url}/adminusersettings/editadminusersetting`
            const res = await fetch(address, {
                method: 'POST',
                headers: this.postHeaders(),
                body: JSON.stringify(data)
            })
               
            if(!res.status.toString().startsWith('2')) {
                onFail()
                return
            }
            onSuccess()
        }
        catch(err) {
            onFail()
        }
    }

    async inviteUser(data: any, onSuccess: () => void, onFail: () => void) {
        try {
            const address = `${this.#url}/adminusersettings/invite`
            const res = await fetch(address, {
                method: 'POST',
                headers: this.postHeaders(),
                body: JSON.stringify(data)
            })
               
            if(res.status && res.status >= 400) {
                onFail()
                return
            }
            onSuccess()
        }
        catch(err) {
            onFail()
        }
    }

    async changeUserRole(userId: number, role: UserRole, onSuccess: () => void, onFail: () => void) {
        try {
            const address = `${this.#url}/adminusersettings/changeuserrole?userId=${userId}&role=${role}`
            const res = await fetch(address, {
                method: 'PUT',
                headers: this.getHeaders(),
            })
               
            if(res.status !== 200) {
                onFail()
                return
            }
            onSuccess()
        }
        catch(err) {
            onFail()
        }
    }

    async getUserData(userUid: string): Promise<{val: (UserExtraData & UserProfile) | null, isAborted: boolean}> {
        try {            
            const address = `${this.#url}/usersettings/getOtherUserFullData?id=${userUid}`
            const res = await fetch(address, {
                method: 'GET',
                headers: this.getHeaders(),
            })

            if (!res) return {isAborted: false, val: null}
            const responseBodyText = await res.json()

            const result: UserExtraData & UserProfile = responseBodyText
            return {
                isAborted: false,
                val: result ?? null
            }

        }
        catch (err) {
            //@ts-ignore
            if (err.name == 'AbortError') {
                return {val: null, isAborted: true}
            }

            return {isAborted: false, val: null}
        }
    }


    async  getSponsoredContent() {
        try {
            const address = `${this.#url}/sponsored`
            const res = await fetch(address, {
                method: 'GET',
                headers: this.getHeaders(),
            })
               
                const result = await res.json()
                console.log('SponsoredContentresult', result )
                return result;
            
        }
        catch(err) {
            //@ts-ignore
            if (err.name === 'AbortError') {
                return { val: [], isAborted: true }
            }
            
            return { val: [], isAborted: false }
        }

    }
}

